/**
 * Idle-time scheduler for mandatory post-processing that must never delay
 * interactive feedback (tool exits, pointer/keyboard response).
 *
 * Heavy-but-required bookkeeping (join solving, profile recomputation,
 * neighbor refreshes) is submitted here instead of running inline: it
 * executes in the background, after the browser has served the pending
 * input/render work, without blocking the action that triggered it.
 *
 * Contracts:
 * - Tasks run in submission order (FIFO).
 * - A task must be self-sufficient: it re-reads current store state, since
 *   unrelated interactions may have mutated the document between submission
 *   and execution (the user may keep drawing, undo, or delete elements).
 * - Cancellation is advisory; tasks must tolerate a skipped run.
 */

/** Fallback delay when the environment has no idle callback API. */
const FALLBACK_DELAY_MS = 0;
/** Cap so a continuously busy main thread still runs the task promptly. */
const MAX_STARVATION_MS = 150;

/** Cancels a pending background task. Safe to call more than once. */
export type CancelBackgroundWork = () => void;

export function scheduleBackgroundWork(task: () => void): CancelBackgroundWork {
    if (typeof window === 'undefined') {
        // Non-DOM consumers (headless tooling): keep deterministic behavior
        // by running the task inline.
        task();
        return () => {};
    }

    let cancelled = false;
    const guarded = () => {
        if (!cancelled) task();
    };

    if (typeof window.requestIdleCallback === 'function') {
        const handle = window.requestIdleCallback(guarded, { timeout: MAX_STARVATION_MS });
        return () => {
            cancelled = true;
            window.cancelIdleCallback(handle);
        };
    }

    const handle = window.setTimeout(guarded, FALLBACK_DELAY_MS);
    return () => {
        cancelled = true;
        window.clearTimeout(handle);
    };
}
